import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

import type { PhraseChip, PhraseChipSelection } from '@/services';
import { cn } from '@/services/utils';

/** The wire value both halves use for ANY. */
const ANY = '*';

/**
 * How far one chip has been relaxed. The two halves are independent: the word half decides whether
 * the card's own word is required, the part-of-speech half decides what class fills the slot.
 * Giving up both leaves a slot that accepts anything, which is what a switched-off chip is — so
 * that combination is the off state rather than a fourth mode.
 */
export interface ChipState {
  /** true — any word of {@link pos} fills the slot, instead of the card's word. */
  anyWord: boolean;
  /** The UPOS tag the slot is held to; null accepts any part of speech. */
  pos: string | null;
}

export type ChipStates = ReadonlyMap<string, ChipState>;

const noStates: ChipStates = new Map();

/** The part of speech the card gave a chip, normalised to the same null-means-any shape. */
const cardPos = (chip: PhraseChip) => chip.pos || null;

const defaultState = (chip: PhraseChip): ChipState => ({ anyWord: false, pos: cardPos(chip) });

export const chipStateFor = (states: ChipStates, chip: PhraseChip): ChipState =>
  states.get(chip.lemma) ?? defaultState(chip);

/** Any word, any part of speech: a free slot, which reads and behaves as switched off. */
export const chipIsOff = (state: ChipState) => state.anyWord && state.pos === null;

const chipIsDefault = (chip: PhraseChip, state: ChipState) =>
  !state.anyWord && state.pos === cardPos(chip);

/**
 * Whether every chip still stands for exactly what the card stored. While that holds the panels can
 * page the precomputed matches; the moment it stops, only a live search can answer.
 */
export const chipsAreDefault = (chips: readonly PhraseChip[], states: ChipStates) =>
  chips.every((chip) => chipIsDefault(chip, chipStateFor(states, chip)));

/** How many chips still require their own word — the wildcards have no lemma to search on. */
const literalChipCount = (chips: readonly PhraseChip[], states: ChipStates) =>
  chips.filter((chip) => !chipStateFor(states, chip).anyWord).length;

export function buildChipSelection(
  chips: readonly PhraseChip[],
  states: ChipStates
): PhraseChipSelection {
  const words: string[] = [];
  const anyWords: string[] = [];
  const posOverrides: string[] = [];

  for (const chip of chips) {
    const state = chipStateFor(states, chip);
    if (chipIsOff(state)) {
      continue;
    }

    words.push(chip.lemma);
    if (state.anyWord) {
      anyWords.push(chip.lemma);
    }
    // Only a genuine change travels; an untouched chip keeps whatever the card stored.
    if (state.pos !== cardPos(chip)) {
      posOverrides.push(`${chip.lemma}:${state.pos ?? ANY}`);
    }
  }

  return { words, anyWords, posOverrides };
}

/**
 * Holds the chip states for one card. Like the panels' other card-scoped state it is dropped during
 * render rather than in an effect: a committed render still carrying the previous card's chips would
 * ask for a selection this card was never offered.
 */
export function usePhraseChips(cardKey: string) {
  const [states, setStates] = useState<ChipStates>(noStates);
  const [heldFor, setHeldFor] = useState(cardKey);

  if (heldFor !== cardKey) {
    setHeldFor(cardKey);
    setStates(noStates);
  }

  const setChipState = (lemma: string, state: ChipState) =>
    setStates((current) => new Map(current).set(lemma, state));

  return { chipStates: heldFor === cardKey ? states : noStates, setChipState };
}

const POS_NAMES: Record<string, string> = {
  NOUN: 'noun',
  VERB: 'verb',
  ADJ: 'adjective',
  ADV: 'adverb',
  ADP: 'preposition',
  AUX: 'auxiliary',
  CCONJ: 'conjunction',
  SCONJ: 'conjunction',
  DET: 'determiner',
  INTJ: 'interjection',
  NUM: 'numeral',
  PART: 'particle',
  PRON: 'pronoun',
  PROPN: 'proper noun',
  X: 'other',
};

const posName = (pos: string) => POS_NAMES[pos] ?? pos.toLowerCase();

/** The four classes worth offering, plus whatever the card actually stored, which may be none of them. */
const posChoices = (chip: PhraseChip): string[] => {
  const base = ['NOUN', 'VERB', 'ADJ', 'ADV'];
  const own = cardPos(chip);
  return own && !base.includes(own) ? [own, ...base] : base;
};

const menuClasses =
  'z-50 rounded-[10px] border border-[#ded9cf] bg-white p-1.5 shadow-[0_12px_28px_rgba(28,34,51,0.14)]';

const MENU_GAP = 6;
const VIEWPORT_MARGIN = 8;
// Fixed rather than intrinsic: the placement maths has to know the width before the menu is laid out.
const WORD_MENU_WIDTH = 224;
const POS_MENU_WIDTH = 208;

/**
 * Pins a menu under its half of the chip, in viewport coordinates. The panels clip their overflow
 * and are only a few hundred pixels wide, so a menu positioned inside the chip is cut off by the
 * panel edge — it goes in a portal instead, and is nudged back inside the viewport rather than
 * anchored to one side of the chip, which would push the leftmost chip's menu off screen.
 */
function useAnchoredMenu(anchor: HTMLElement | null, open: boolean, width: number) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ position: 'fixed', visibility: 'hidden' });

  useLayoutEffect(() => {
    if (!open || !anchor) {
      return;
    }

    const place = () => {
      const rect = anchor.getBoundingClientRect();
      const height = menuRef.current?.offsetHeight ?? 0;
      const left = Math.max(
        VIEWPORT_MARGIN,
        Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN)
      );
      // Below by default, flipped above only when there is room up there and not down here.
      const below = rect.bottom + MENU_GAP;
      const above = rect.top - MENU_GAP - height;
      const top =
        below + height > window.innerHeight - VIEWPORT_MARGIN && above > VIEWPORT_MARGIN
          ? above
          : below;

      setStyle({ position: 'fixed', top, left, width });
    };

    place();
    // Capture, so scrolling the panel itself moves the menu with its chip, not just the window.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);

    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, anchor, width]);

  return { menuRef, style };
}

const menuHeadingClasses =
  'px-2.5 pb-1 pt-1.5 font-mono text-[10px] font-medium uppercase leading-none tracking-[0.09em] text-[#97917f]';

const rowClasses =
  'flex w-full items-center gap-2 rounded-[7px] px-2.5 py-2 text-left transition enabled:cursor-pointer enabled:hover:bg-[#f2efe9] disabled:cursor-not-allowed disabled:opacity-45';

function MenuRow({
  selected,
  disabled,
  title,
  onSelect,
  children,
}: {
  selected: boolean;
  disabled?: boolean;
  title?: string;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      disabled={disabled}
      title={title}
      onClick={onSelect}
      className={cn(rowClasses, selected && 'bg-[#f2efe9]')}
    >
      {children}
    </button>
  );
}

interface PhraseChipProps {
  chip: PhraseChip;
  state: ChipState;
  /** The chip holds the last word the search can seek on, so it may not give that word up. */
  lastLiteral: boolean;
  openHalf: 'word' | 'pos' | null;
  onOpenChange: (half: 'word' | 'pos' | null) => void;
  onChange: (state: ChipState) => void;
}

function PhraseChipControl({
  chip,
  state,
  lastLiteral,
  openHalf,
  onOpenChange,
  onChange,
}: PhraseChipProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wordButtonRef = useRef<HTMLButtonElement>(null);
  const posButtonRef = useRef<HTMLButtonElement>(null);
  const off = chipIsOff(state);
  const own = cardPos(chip);
  // Read during render rather than in an effect: the buttons mount before any menu can open, so by
  // the render that opens one the ref already points at the half that was clicked.
  const anchor =
    openHalf === 'word' ? wordButtonRef.current : openHalf === 'pos' ? posButtonRef.current : null;
  const { menuRef, style: menuStyle } = useAnchoredMenu(
    anchor,
    openHalf !== null,
    openHalf === 'word' ? WORD_MENU_WIDTH : POS_MENU_WIDTH
  );

  useEffect(() => {
    if (!openHalf) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // The menu lives in a portal, so it is not inside the chip: both have to be checked.
      if (
        !containerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        onOpenChange(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }

      onOpenChange(null);
      (openHalf === 'word' ? wordButtonRef : posButtonRef).current?.focus();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openHalf, onOpenChange, menuRef]);

  const choose = (next: ChipState) => {
    onChange(next);
    onOpenChange(null);
  };

  const lockedTitle = 'At least one word has to keep its own word';
  const anyWordLabel = state.pos ? `any ${posName(state.pos)}` : 'any word';

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          'inline-flex items-stretch overflow-hidden rounded-full',
          off ? 'border border-dashed border-slate-300 bg-slate-100' : 'bg-slate-800'
        )}
      >
        <button
          ref={wordButtonRef}
          type="button"
          onClick={() => onOpenChange(openHalf === 'word' ? null : 'word')}
          aria-haspopup="menu"
          aria-expanded={openHalf === 'word'}
          aria-label={`Word: ${state.anyWord ? anyWordLabel : chip.lemma}`}
          className={cn(
            'flex cursor-pointer items-center gap-1 py-1.5 pl-3 pr-2 text-[13px] font-medium leading-tight outline-none transition focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400',
            off && 'text-slate-400 line-through',
            !off && state.anyWord && 'italic text-white/60 hover:bg-white/10',
            !off && !state.anyWord && 'text-white hover:bg-white/10'
          )}
        >
          {state.anyWord ? 'any' : chip.lemma}
          <ChevronDown aria-hidden className="size-3 shrink-0 opacity-70" />
        </button>
        <button
          ref={posButtonRef}
          type="button"
          onClick={() => onOpenChange(openHalf === 'pos' ? null : 'pos')}
          aria-haspopup="menu"
          aria-expanded={openHalf === 'pos'}
          aria-label={`Part of speech: ${state.pos ? posName(state.pos) : 'any'}`}
          className={cn(
            'flex cursor-pointer items-center gap-1 py-1.5 pl-2.5 pr-2.5 font-mono text-[11px] font-semibold uppercase leading-none tracking-[0.06em] outline-none transition focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400',
            off ? 'bg-black/5 text-slate-400' : 'bg-white/12 text-white/70 hover:bg-white/20'
          )}
        >
          {state.pos ?? 'ANY'}
          <ChevronDown aria-hidden className="size-3 shrink-0 opacity-70" />
        </button>
      </div>

      {openHalf === 'word' && createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          role="menu"
          aria-label={`${chip.lemma}: word`}
          className={menuClasses}
        >
          <div className={menuHeadingClasses}>Word</div>
          <MenuRow
            selected={!state.anyWord}
            onSelect={() => choose({ ...state, anyWord: false })}
          >
            <span className="text-[13px] font-medium text-slate-900">{chip.lemma}</span>
          </MenuRow>
          <MenuRow
            selected={state.anyWord && !off}
            disabled={lastLiteral}
            title={lastLiteral ? lockedTitle : undefined}
            onSelect={() => choose({ ...state, anyWord: true })}
          >
            <span className="text-[13px] font-medium italic text-slate-900">{anyWordLabel}</span>
          </MenuRow>
          <div aria-hidden className="mx-1 my-1.5 h-px bg-[#efece5]" />
          <MenuRow
            selected={off}
            disabled={lastLiteral}
            title={lastLiteral ? lockedTitle : undefined}
            onSelect={() => choose({ anyWord: true, pos: null })}
          >
            <span aria-hidden className="w-4 text-center font-mono text-xs text-[#c3bcae]">
              ○
            </span>
            <span className="text-[13px] font-medium text-slate-900">Turn off</span>
          </MenuRow>
        </div>,
        document.body
      )}

      {openHalf === 'pos' && createPortal(
        <div
          ref={menuRef}
          style={menuStyle}
          role="menu"
          aria-label={`${chip.lemma}: part of speech`}
          className={menuClasses}
        >
          <div className={menuHeadingClasses}>Part of speech</div>
          {posChoices(chip).map((pos) => (
            <MenuRow
              key={pos}
              selected={state.pos === pos}
              onSelect={() => choose({ ...state, pos })}
            >
              <span className="flex-1 text-[13px] font-medium text-slate-900">{posName(pos)}</span>
              <span className="font-mono text-[9.5px] font-semibold text-[#97917f]">
                {pos}
                {pos === own && ' · card'}
              </span>
            </MenuRow>
          ))}
          <MenuRow selected={state.pos === null} onSelect={() => choose({ ...state, pos: null })}>
            <span className="flex-1 text-[13px] font-medium text-slate-900">any</span>
            <span className="font-mono text-[9.5px] font-semibold text-[#97917f]">{ANY}</span>
          </MenuRow>
        </div>,
        document.body
      )}
    </div>
  );
}

interface PhraseChipRowProps {
  chips: readonly PhraseChip[];
  states: ChipStates;
  onChipChange: (lemma: string, state: ChipState) => void;
  label: string;
}

/**
 * The panels' phrase filter: one two-part chip per word of the card. A chip reads as its own rule
 * with no menu open — the word on the left, the class it is held to on the right, dashed and struck
 * through once it stops constraining anything.
 */
export function PhraseChipRow({ chips, states, onChipChange, label }: PhraseChipRowProps) {
  // One menu at a time across the whole row, keyed by chip and half.
  const [open, setOpen] = useState<string | null>(null);
  const literals = literalChipCount(chips, states);

  return (
    <div role="group" aria-label={label} className="mt-2 flex flex-wrap items-center gap-1.5">
      {chips.map((chip) => {
        const state = chipStateFor(states, chip);
        return (
          <PhraseChipControl
            key={chip.lemma}
            chip={chip}
            state={state}
            lastLiteral={!state.anyWord && literals === 1}
            openHalf={
              open === `${chip.lemma}:word` ? 'word' : open === `${chip.lemma}:pos` ? 'pos' : null
            }
            onOpenChange={(half) => setOpen(half ? `${chip.lemma}:${half}` : null)}
            onChange={(next) => onChipChange(chip.lemma, next)}
          />
        );
      })}
    </div>
  );
}
